// Copyright (c) 2017 VMware, Inc. All Rights Reserved.
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//    http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.
import { Component, OnInit, ViewChild, Input, Output, EventEmitter } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { State } from '@clr/angular';

import { RepositoryService } from '../service/repository.service';
import { Repository, RepositoryItem, Tag, TagClickEvent,
  SystemInfo, SystemInfoService, TagService } from '../service/index';
import { ErrorHandler } from '../error-handler/index';
import { ConfirmationState, ConfirmationTargets } from '../shared/shared.const';
import { ConfirmationDialogComponent, ConfirmationMessage, ConfirmationAcknowledgement } from '../confirmation-dialog/index';
import { toPromise } from '../utils';

const TabLinkContentMap: {[index: string]: string} = {
  'repo-info': 'info',
  'repo-image': 'image'
};

@Component({
  selector: 'hbr-repository',
  template: `<section class="overview-section">
  <div class="title-wrapper">
    <div class="title-block arrow-block" *ngIf="withAdmiral">	
       <clr-icon class="rotate-90 arrow-back" shape="arrow" size="36" (click)="goBack()"></clr-icon>
    </div>
    <div class="title-block">
      <h2 sub-header-title class="custom-h2">{{repoName}}</h2>
    </div>
  </div>
</section>

<section class="detail-section">
  <div class="col-lg-12 col-md-12 col-sm-12 col-xs-12">
    <span class="spinner spinner-inline" [hidden]="inProgress === false"></span>
    <ul id="configTabs" class="nav" role="tablist">
      <li role="presentation" class="nav-item">
          <button id="repo-info" class="btn btn-link nav-link" aria-controls="info" [class.active]='isCurrentTabLink("repo-info")' type="button" (click)='tabLinkClick("repo-info")'>{{'REPOSITORY.INFO' | translate}}</button>
      </li>
      <li role="presentation" class="nav-item">
          <button id="repo-image" class="btn btn-link nav-link active" aria-controls="image" [class.active]='isCurrentTabLink("repo-image")' type="button" (click)='tabLinkClick("repo-image")'>{{'REPOSITORY.IMAGE' | translate}}</button>
      </li>
    </ul>
    <section id="info" role="tabpanel" aria-labelledby="repo-info" [hidden]='!isCurrentTabContent("info")'>
      <form #repoInfoForm="ngForm">
        <div id="info-edit-button">
          <button class="btn btn-sm" [disabled]="editing || !hasProjectAdminRole " (click)="editInfo()" ><clr-icon shape="pencil" size="16"></clr-icon>&nbsp;{{'BUTTON.EDIT' | translate}}</button>
        </div>
        <div *ngIf="!editing">
          <div *ngIf="!hasInfo()" class="no-info-div">
            <p>{{'REPOSITORY.NO_INFO' | translate }}<p>
          </div>
          <div *ngIf="hasInfo()" class="info-div">
            <pre class="info-pre">{{ imageInfo }}</pre>
          </div>
        </div>
        <div *ngIf="editing">
            <textarea rows="5"  name="info-edit-textarea" [(ngModel)]="imageInfo"></textarea>
        </div>
        <div class="btn-sm" *ngIf="editing">
          <button class="btn btn-primary" [disabled]="!hasChanges()" (click)="saveInfo()" >{{'BUTTON.SAVE' | translate}}</button>
          <button class="btn" (click)="cancelInfo()" >{{'BUTTON.CANCEL' | translate}}</button>
        </div>
        <confirmation-dialog #confirmationDialog (confirmAction)="confirmCancel($event)"></confirmation-dialog>
      </form>
    </section>
    <section id="image" role="tabpanel" aria-labelledby="repo-image" [hidden]='!isCurrentTabContent("image")'>
      <div id=images-container>
        <hbr-tag ngProjectAs="clr-dg-row-detail" (tagClickEvent)="watchTagClickEvt($event)" (signatureOutput)="saveSignatures($event)" class="sub-grid-custom" [repoName]="repoName" [registryUrl]="registryUrl" [withNotary]="withNotary" [withClair]="withClair" [withAdmiral]="withAdmiral" [hasSignedIn]="hasSignedIn" [hasProjectAdminRole]="hasProjectAdminRole" [isGuest]="isGuest" [projectId]="projectId"></hbr-tag>
      </div>
    </section>
  </div>
</section>`,
  styles: [`.option-right{padding-right:16px;margin-bottom:12px}.arrow-back{cursor:pointer}.arrow-block{border-right:2px solid #ccc;margin-right:6px;display:inline-flex;padding:6px 6px 6px 12px}.title-block{display:inline-block}.tag-name{font-weight:300;font-size:32px}.no-info-div{background:#fff;border:1px solid #ccc;padding:12px}.info-div{background:#fff;border:1px solid #ccc;padding:0 12px 24px}.info-pre{border:0;max-height:-webkit-fit-content;max-height:-moz-fit-content;max-height:fit-content}#info-edit-button{margin-top:0;margin-bottom:12px}#images-container{margin-top:12px}harbor-tag{position:relative;top:24px}`]
})
export class RepositoryComponent implements OnInit {
  signedCon: {[key: string]: any | string[]} = {};
  @Input() projectId: number;
  @Input() repoName: string;
  @Input() hasSignedIn: boolean;
  @Input() hasProjectAdminRole: boolean;
  @Input() isGuest: boolean;
  @Output() tagClickEvent = new EventEmitter<TagClickEvent>();
  @Output() backEvt: EventEmitter<any> = new EventEmitter<any>();

  onGoing = false;
  editing = false;
  inProgress = true;
  currentTabID = 'repo-image';
  changedRepositories: RepositoryItem[];
  systemInfo: SystemInfo;

  imageInfo: string;
  orgImageInfo: string;

  timerHandler: any;

  @ViewChild('confirmationDialog')
  confirmationDlg: ConfirmationDialogComponent;

  constructor(
    private errorHandler: ErrorHandler,
    private repositoryService: RepositoryService,
    private systemInfoService: SystemInfoService,
    private tagService: TagService,
    private translate: TranslateService,
  ) {  }

  public get registryUrl(): string {
    return this.systemInfo ? this.systemInfo.registry_url : '';
  }

  public get withNotary(): boolean {
    return this.systemInfo ? this.systemInfo.with_notary : false;
  }

  public get withClair(): boolean {
    return this.systemInfo ? this.systemInfo.with_clair : false;
  }

  public get withAdmiral(): boolean {
    return this.systemInfo ? this.systemInfo.with_admiral : false;
  }

  ngOnInit(): void {
    if (!this.projectId) {
      this.errorHandler.error('Project ID cannot be unset.');
      return;
    }
    this.retrieve();
    this.inProgress = false;
  }

  retrieve(state?: State) {
    toPromise<Repository>(this.repositoryService.getRepositories(this.projectId, this.repoName))
      .then( response => {
        if (response.metadata.xTotalCount > 0) {
          this.orgImageInfo = response.data[0].description;
          this.imageInfo = response.data[0].description;
        }
      })
      .catch(error => this.errorHandler.error(error));
    toPromise<SystemInfo>(this.systemInfoService.getSystemInfo())
      .then(systemInfo => this.systemInfo = systemInfo)
      .catch(error => this.errorHandler.error(error));
  }

  saveSignatures(event: {[key: string]: string[]}): void {
    Object.assign(this.signedCon, event);
  }

  refresh() {
    this.retrieve();
  }

  watchTagClickEvt(tagClickEvt: TagClickEvent): void {
    this.tagClickEvent.emit(tagClickEvt);
  }

  isCurrentTabLink(tabID: string): boolean {
    return this.currentTabID === tabID;
  }

  isCurrentTabContent(ContentID: string): boolean {
    return TabLinkContentMap[this.currentTabID] === ContentID;
  }

  tabLinkClick(tabID: string) {
    this.currentTabID = tabID;
  }

  getTagInfo(repoName: string): Promise<void> {
    // this.signedNameArr = [];
   this.signedCon[repoName] = [];
    return toPromise<Tag[]>(this.tagService
           .getTags(repoName))
           .then(items => {
             items.forEach((t: Tag) => {
               if (t.signature !== null) {
                 this.signedCon[repoName].push(t.name);
               }
             });
           })
           .catch(error => this.errorHandler.error(error));
 }

    goBack(): void {
        this.backEvt.emit(this.projectId);
    }

  hasChanges() {
    return this.imageInfo !== this.orgImageInfo;
  }

  reset(): void {
    this.imageInfo = this.orgImageInfo;
  }

  hasInfo() {
    return this.imageInfo && this.imageInfo.length > 0;
  }

  editInfo() {
    this.editing = true;
  }

  saveInfo() {
    if (!this.hasChanges()) {
      return;
    }
    this.onGoing = true;
    toPromise<any>(this.repositoryService.updateRepositoryDescription(this.repoName, this.imageInfo))
      .then(() => {
        this.onGoing = false;
        this.translate.get('CONFIG.SAVE_SUCCESS').subscribe((res: string) => {
          this.errorHandler.info(res);
        });
        this.editing = false;
        this.refresh();
      })
      .catch(error => {
        this.onGoing = false;
        this.errorHandler.error(error);
      });
  }

  cancelInfo() {
    let msg = new ConfirmationMessage(
      'CONFIG.CONFIRM_TITLE',
      'CONFIG.CONFIRM_SUMMARY',
      '',
      {},
      ConfirmationTargets.CONFIG
    );
    this.confirmationDlg.open(msg);
  }

  confirmCancel(ack: ConfirmationAcknowledgement): void {
    this.editing = false;
    if (ack && ack.source === ConfirmationTargets.CONFIG &&
        ack.state === ConfirmationState.CONFIRMED) {
        this.reset();
    }
  }
}
